import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Skeleton,
} from "@mui/material";
import axios from "axios";
import {
  AutoCompleteComponent,
  CurrencyNumberInput,
  DatePicker,
  FormLabel,
  ModalError,
  ModalSuccess,
  NumberInput,
  PrimaryButton,
  TextInput,
  SupportingDocumentForm,
} from "components";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { hardBaseUrl } from "services/urlConstant";
import { formatPayloadDate, getErrors } from "utils";

const ModalFormLoan = ({
  open,
  onClose,
  preload,
  getDataTable,
  modalFor,
  optionComposer,
  userLogin,
}) => {
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const userComposer = userLogin?.role?.name === "User Composer";
  const publisherName = userLogin?.publisher?.name || "";

  const [loadingPage, setLoadingPage] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [payload, setPayload] = useState({
    date: moment(),
    value: 0,
    tenor: 0,
    note: "",
    ...(!userComposer && { composer_id: 0 }),
  });

  const handleChangePayload = (value, key) => {
    setPayload(currentState => ({
      ...currentState,
      [key]: value,
    }));
  };

  const getDetail = async () => {
    setLoadingPage(true);
    try {
      const res = await axios.get(
        `${hardBaseUrl}/publisher/transaction-loan/${preload}`,
        { headers }
      );
      const { data } = res?.data;
      const requiredKeys = ["date", "composer_id", "id"];

      const filteredData = requiredKeys?.reduce((acc, key) => {
        acc[key] = data[key];
        return acc;
      }, {});
      setPayload({
        note: data?.notes,
        value: data?.transaction_value_number,
        tenor: data?.tenor_number,
        ...filteredData,
      });
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const handleSubmit = async () => {
    const url = userComposer
      ? "composer/transaction/loan"
      : "publisher/transaction-loan";
    const method = preload ? axios.put : axios.post;
    const { date, ...restPayload } = payload;

    const modifiedPayload = {
      ...restPayload,
      date: formatPayloadDate(date),
      ...(preload && { id: preload }),
    };
    try {
      setLoadingButton(true);
      await method(`${hardBaseUrl}/${url}`, modifiedPayload, { headers });
      ModalSuccess(
        `Successfully ${preload ? "update" : "save"} loan`
      ).then(() => getDataTable());
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingButton(false);
      onClose();
    }
  };

  useEffect(() => {
    if (preload) {
      getDetail();
    }
  }, [preload]);

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle>{`${preload ? "Edit" : "Create"} ${modalFor}`}</DialogTitle>
      <DialogContent dividers>
        {loadingPage ? (
          <Grid container direction="column" rowSpacing={2}>
            {Object.keys(payload)?.map((_, index) => (
              <Grid item key={index}>
                <Box mb="8px">
                  <Skeleton variant="rounded" width="40%" height={25} />
                </Box>
                <Skeleton variant="rounded" width="100%" height={30} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container direction="column" rowSpacing={2}>
            {MockFieldForm({ preload, userComposer })?.map(
              ({ title, key, type, prefix, suffix, disabled }, index) => (
                <Grid item key={index}>
                  <FormLabel label={title} required={key !== "note"} />
                  {type === "autocomplete" && optionComposer?.length ? (
                    <AutoCompleteComponent
                      placeholder={title}
                      options={optionComposer}
                      value={
                        optionComposer.find(
                          option => option.id === payload?.composer_id
                        ) || null
                      }
                      onChange={value =>
                        handleChangePayload(value, "composer_id")
                      }
                      size="small"
                      width="100%"
                      disabled={disabled || loadingButton}
                    />
                  ) : type === "date" ? (
                    <DatePicker
                      value={payload?.date}
                      onChange={date => handleChangePayload(date, "date")}
                      disabled={disabled || loadingButton}
                    />
                  ) : type === "number" ? (
                    <NumberInput
                      value={payload?.[key]}
                      onChange={val => handleChangePayload(Number(val), key)}
                      startAdornment={prefix}
                      endAdornment={suffix}
                      disabled={disabled || loadingButton}
                    />
                  ) : type === "currency" ? (
                    <CurrencyNumberInput
                      value={payload?.[key]}
                      onChange={e => handleChangePayload(e, key)}
                      decimalScale={2}
                      startAdornment={prefix}
                      endAdornment={suffix}
                      disabled={disabled || loadingButton}
                    />
                  ) : type === "supporting_document" ? (
                    <SupportingDocumentForm
                      supportingDocumentFiles={payload?.[key] || []}
                      setSupportingDocumentFiles={files => handleChangePayload(files, key)}
                      disabled={disabled || loadingButton}
                      useBorder={false}
                      useDefaultLabel={false}
                    />
                  ) : (
                    <TextInput
                      placeholder={title}
                      value={
                        key === "publisher" ? publisherName : payload?.[key]
                      }
                      autoComplete={`new-${key}`}
                      onChange={e => handleChangePayload(e?.target?.value, key)}
                      multiline={key === "note"}
                      rows={key === "note" ? 4 : 1}
                      disabled={disabled || loadingButton}
                    />
                  )}
                </Grid>
              )
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          label={loadingButton ? "Saving" : "Save as Draft"}
          loading={loadingButton}
          disabled={loadingPage || loadingButton}
          onClick={() => console.log("save as draft")}
        />
        <PrimaryButton
          label={loadingButton ? "Saving" : "Send to Approval"}
          loading={loadingButton}
          disabled={loadingPage || loadingButton}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

const MockFieldForm = ({ preload, userComposer }) => [
  {
    title: "Date",
    key: "date",
    type: "date",
    disabled: preload,
  },
  ...(userComposer
    ? [
        {
          title: "Publisher",
          key: "publisher",
          disabled: true,
        },
      ]
    : [
        {
          title: "Composer/ Author",
          key: "composer_id",
          type: "autocomplete",
          disabled: preload,
        },
      ]),
  {
    title: "Tenor",
    key: "tenor",
    type: "number",
    suffix: "Months",
  },
  {
    title: "Loan Value",
    key: "value",
    type: "currency",
    prefix: "Rp",
  },
  {
    title: "Notes",
    key: "note",
    disabled: preload,
  },
  {
    title: "Supporting Document",
    key: "supporting_document",
    type: "supporting_document",
  },
];
export default ModalFormLoan;
